import { useState } from "react";
import { Link } from "react-router-dom";
import { Lock, ArrowRight, ClipboardList, PackageSearch } from "lucide-react";

const DealerLoginPage = () => {
  const [form, setForm] = useState({ email: "", password: "" });
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
  };

  const inputCls =
    "w-full border-b border-brandgray bg-transparent py-3 px-0 focus:outline-none focus:border-navy transition-colors text-black placeholder:text-black/40 rounded-none";

  return (
    <div data-testid="dealer-login-page" className="bg-ivory min-h-screen">
      <section className="pt-32 md:pt-40 pb-20 md:pb-28">
        <div className="max-w-6xl mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* LEFT — copy + benefits */}
          <div className="lg:col-span-6 reveal">
            <div className="overline mb-4">Dealer portal</div>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight font-light">
              For existing<br />
              <span className="italic text-navy">Tarush dealers.</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-black/70 leading-relaxed max-w-lg">
              A private login for our dealer network — coming soon.
              Once live, approved dealers will be able to build instant
              quotations and track live order status from a single dashboard.
            </p>

            <div className="mt-10 space-y-4 max-w-lg">
              <div className="flex gap-4 items-start bg-white border-l-4 border-teal p-5">
                <ClipboardList size={22} className="text-teal shrink-0 mt-0.5" />
                <div>
                  <div className="font-display text-xl text-navy leading-tight">Quotation Tool</div>
                  <p className="text-sm text-black/70 mt-1">
                    Build client quotes from the live catalog — pick category,
                    specify materials, get an instant priced BOM to share as PDF.
                  </p>
                </div>
              </div>
              <div className="flex gap-4 items-start bg-white border-l-4 border-navy p-5">
                <PackageSearch size={22} className="text-navy shrink-0 mt-0.5" />
                <div>
                  <div className="font-display text-xl text-navy leading-tight">Order Status Tracking</div>
                  <p className="text-sm text-black/70 mt-1">
                    Follow every order from confirmed → cut → finished → dispatched
                    → installed, with delivery ETAs and factory notes.
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-10 text-sm text-black/60">
              Not a dealer yet?{" "}
              <Link
                to="/become-a-dealer"
                data-testid="dealer-login-become-dealer"
                className="text-navy border-b border-navy/40 hover:border-navy pb-0.5"
              >
                Apply to join the network
              </Link>
            </div>
          </div>

          {/* RIGHT — login card */}
          <div className="lg:col-span-6 lg:col-start-8 reveal">
            <div className="bg-white p-8 md:p-10 shadow-[0_24px_48px_-24px_rgba(41,72,144,0.18)] ring-1 ring-brandgray">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-9 h-9 rounded-full bg-navy/5 flex items-center justify-center">
                  <Lock size={16} className="text-navy" />
                </div>
                <div>
                  <div className="font-display text-2xl text-navy leading-tight">Sign in</div>
                  <div className="text-xs text-black/50 tracking-wide">Dealer access only</div>
                </div>
              </div>

              {submitted ? (
                <div
                  data-testid="dealer-login-coming-soon"
                  className="bg-cream border border-brandgray p-6 text-center"
                >
                  <div className="overline mb-3" style={{ color: "#2A6F73" }}>
                    Launching Shortly
                  </div>
                  <div className="font-display text-2xl text-navy leading-tight">
                    Dealer portal opens soon.
                  </div>
                  <p className="mt-3 text-sm text-black/70">
                    We're onboarding our first cohort of dealers. Reach out and
                    we'll notify you the moment logins go live.
                  </p>
                  <Link
                    to="/contact"
                    data-testid="dealer-login-contact-cta"
                    className="mt-6 inline-flex items-center gap-2 bg-coral hover:bg-[#d66a54] text-white text-sm px-5 py-2.5 tracking-wide transition-colors"
                  >
                    <span>Get in Touch</span>
                    <ArrowRight size={16} />
                  </Link>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="space-y-6" data-testid="dealer-login-form">
                  <div>
                    <div className="overline mb-2">Email</div>
                    <input
                      type="email"
                      data-testid="dealer-login-email"
                      className={inputCls}
                      value={form.email}
                      onChange={update("email")}
                      placeholder="you@dealer.com"
                      required
                    />
                  </div>
                  <div>
                    <div className="overline mb-2">Password</div>
                    <input
                      type="password"
                      data-testid="dealer-login-password"
                      className={inputCls}
                      value={form.password}
                      onChange={update("password")}
                      placeholder="••••••••"
                      required
                    />
                  </div>

                  <button
                    type="submit"
                    data-testid="dealer-login-submit"
                    className="w-full inline-flex items-center justify-center gap-2 bg-navy hover:bg-navy/90 text-white text-sm px-5 py-3 tracking-wide transition-colors"
                  >
                    <span>Sign in</span>
                    <ArrowRight size={16} />
                  </button>

                  <div className="text-xs text-black/50 text-center">
                    Trouble signing in?{" "}
                    <Link to="/contact" className="text-navy border-b border-navy/40">
                      Contact support
                    </Link>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DealerLoginPage;
